import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Download,
  Search,
  Lock,
  Globe,
  User,
  ListChecks,
  Loader2,
} from 'lucide-react';
import { Button } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminBookings, useAdminBookingStatistics, useExportAdminBookings } from '../../services/adminBookingService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const SOURCE_TABS = [
  { key: 'platform', label: 'Platform bookings', icon: Globe },
  { key: 'private', label: 'Private bookings', icon: Lock },
];

const isPrivateBooking = (booking) => booking.booking_source === 'vendor_manual';

const getClientDisplayName = (booking) => {
  if (isPrivateBooking(booking)) {
    return booking.guest_name || booking.manual_title || 'Private client';
  }
  return booking.client_display_name || booking.client?.name || 'Unknown Client';
};

const AdminBookings = () => {
  const navigate = useNavigate();
  const [sourceTab, setSourceTab] = useState('platform');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    date_from: '',
    date_to: ''
  });

  const isPrivateTab = sourceTab === 'private';

  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
  );

  const apiFilters = {
    ...cleanFilters,
    booking_source: isPrivateTab ? 'vendor_manual' : 'platform',
    per_page: 50,
    ...(isPrivateTab && filters.search ? { guest_search: filters.search } : {}),
    ...(!isPrivateTab && filters.search ? { client_search: filters.search } : {}),
  };
  delete apiFilters.search;

  const { data: bookingsResponse, isLoading, error } = useAdminBookings(apiFilters);
  const { data: statsResponse } = useAdminBookingStatistics();

  const stats = bookingsResponse?.statistics || statsResponse?.data || {};
  const tabTotal = bookingsResponse?.data?.total ?? 0;

  const allBookings = bookingsResponse?.data?.data || [];
  const bookings = allBookings.filter((booking) => booking.status !== 'pending');

  const exportBookingsMutation = useExportAdminBookings({
    onSuccess: () => {
      console.log('Bookings exported successfully');
    },
    onError: (exportError) => {
      console.error('Failed to export bookings:', exportError);
    }
  });

  const getStatusConfig = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300', icon: Clock, label: 'Pending' },
      confirmed: { color: 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300', icon: CheckCircle, label: 'Confirmed' },
      in_progress: { color: 'bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-300', icon: Clock, label: 'In Progress' },
      completed: { color: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300', icon: CheckCircle, label: 'Completed' },
      cancelled: { color: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300', icon: XCircle, label: 'Cancelled' },
      no_show: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300', icon: XCircle, label: 'No Show' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const truncateText = (text, maxLength = 12) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const columns = useMemo(() => [
    {
      key: 'booking_reference',
      label: 'Booking #',
      sortable: true,
      width: 'w-32',
      render: (booking) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={`font-mono text-xs font-medium truncate block ${
              isPrivateTab
                ? 'text-gray-800 dark:text-gray-200'
                : 'text-primary-600 dark:text-primary-400'
            }`}
            title={`#${booking.booking_reference}`}
          >
            #{truncateText(booking.booking_reference, 12)}
          </span>
          {isPrivateTab && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-semibold uppercase bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 flex-shrink-0">
              <Lock className="w-2 h-2" />
            </span>
          )}
        </div>
      )
    },
    {
      key: 'vendor',
      label: 'Vendor',
      sortable: true,
      width: 'w-48',
      render: (booking) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
            {booking.vendor?.business_name || 'Unknown Vendor'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
            {booking.custom_service_name || booking.vendor_service?.service_name || 'Unknown Service'}
          </p>
        </div>
      )
    },
    {
      key: 'client',
      label: isPrivateTab ? 'Guest client' : 'Client',
      sortable: true,
      width: 'w-48',
      render: (booking) => {
        const isPrivate = isPrivateTab || isPrivateBooking(booking);
        const displayName = getClientDisplayName(booking);
        const subline = isPrivate
          ? booking.guest_email || booking.guest_phone || 'Vendor private booking'
          : booking.client?.email;

        return (
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                isPrivate
                  ? 'bg-gray-200 dark:bg-gray-700'
                  : 'bg-gray-100 dark:bg-gray-800'
              }`}
            >
              {isPrivate ? (
                <Lock className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
              ) : (
                <User className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">{displayName}</p>
              {subline && (
                <p className="text-xs truncate mt-0.5 text-gray-500 dark:text-gray-400">
                  {subline}
                </p>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'event_date',
      label: 'Date',
      sortable: true,
      width: 'w-32',
      render: (booking) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {formatDate(booking.event_date, 'medium')}
            </div>
            {booking.start_time && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {formatTime(booking.start_time)}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'total_amount',
      label: 'Amount',
      sortable: true,
      width: 'w-24',
      render: (booking) => {
        const formatCurrency = (value) => {
          const num = parseFloat(value);
          if (isNaN(num) || num === null || num === undefined) {
            return '0.00';
          }
          return num.toFixed(2);
        };

        const subtotal = parseFloat(booking.subtotal_amount || 0);
        const platformFee = parseFloat(booking.platform_fee || 0);
        const totalAmountDue = subtotal + platformFee;

        const completedPayments = booking.payments?.filter(p => p.status === 'completed') || [];
        const totalAmountPaid = completedPayments.reduce((sum, payment) => {
          return sum + parseFloat(payment.amount || 0);
        }, 0);

        const depositAmountPaid = completedPayments
          .filter(p => p.payment_type === 'deposit')
          .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);

        const hasDepositPayment = completedPayments.some(p => p.payment_type === 'deposit');
        const hasFullPayment = completedPayments.some(p => p.payment_type === 'full_payment');
        const isFullyPaid = totalAmountPaid >= totalAmountDue && totalAmountDue > 0;

        const showDepositAmount = hasDepositPayment && !hasFullPayment && !isFullyPaid;
        const amountToDisplay = showDepositAmount
          ? depositAmountPaid
          : (completedPayments.length > 0
              ? totalAmountPaid
              : (isPrivateTab ? subtotal : totalAmountDue));

        return (
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
              ${formatCurrency(amountToDisplay)}
            </p>
            {showDepositAmount && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Deposit</p>
            )}
            {!showDepositAmount && completedPayments.length > 0 && !isFullyPaid && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                of ${formatCurrency(totalAmountDue)}
              </p>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (booking) => {
        const statusConfig = getStatusConfig(booking.status);
        const StatusIcon = statusConfig.icon;
        return (
          <span className="whitespace-nowrap flex-shrink-0">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </span>
          </span>
        );
      }
    }
  ], [isPrivateTab]);

  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (booking) => {
        navigate(`/admin/bookings/${booking.id}`);
      }
    }
  ];

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;

    return data.filter(booking =>
      booking.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vendor?.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.guest_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.guest_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.manual_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vendor_service?.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.custom_service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.event_location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      status: '',
      search: '',
      date_from: '',
      date_to: ''
    });
  };

  const handleExport = () => {
    exportBookingsMutation.mutate(apiFilters);
  };

  if (error) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error loading bookings</h3>
          <p className="text-red-600 dark:text-red-300 text-sm">Unable to load bookings data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="All Bookings"
        subtitle="Manage platform and vendor private bookings across the marketplace."
        actions={
          <Button
            variant="outline"
            icon={Download}
            onClick={handleExport}
            disabled={exportBookingsMutation.isPending}
            className="!rounded-xl"
          >
            {exportBookingsMutation.isPending ? 'Exporting...' : 'Export'}
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.bookings.title}
        steps={ADMIN_PAGE_GUIDES.bookings.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
            aria-label="Booking source"
          >
            {SOURCE_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = sourceTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setSourceTab(tab.key)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {isActive && (
                    <span className="text-xs text-gray-400 font-normal tabular-nums">({tabTotal})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading bookings…</p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: '', label: 'All', count: stats.total_bookings || 0 },
                  { value: 'pending', label: 'Pending', count: stats.pending_bookings || 0 },
                  { value: 'confirmed', label: 'Confirmed', count: stats.confirmed_bookings || 0 },
                  { value: 'in_progress', label: 'In progress', count: stats.in_progress_bookings || 0 },
                  { value: 'completed', label: 'Completed', count: stats.completed_bookings || 0 },
                  { value: 'cancelled', label: 'Cancelled', count: stats.cancelled_bookings || 0 },
                  { value: 'no_show', label: 'No show', count: stats.no_show_bookings || 0 }
                ].map((tab) => (
                  <button
                    key={tab.value || 'all'}
                    type="button"
                    onClick={() => handleFilterChange('status', tab.value)}
                    className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-colors border ${
                      filters.status === tab.value
                        ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                        : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {tab.label}{' '}
                    <span className="tabular-nums opacity-70">({tab.count})</span>
                  </button>
                ))}
              </div>

              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Filters</h3>
                  <Button variant="outline" size="sm" className="!rounded-lg" onClick={clearAllFilters}>
                    Clear all
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {isPrivateTab ? 'Search guest' : 'Search client'}
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        placeholder={isPrivateTab ? 'Guest name, email, title...' : 'Client name or email...'}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div>
                    <DatePicker
                      label="From Date"
                      value={filters.date_from}
                      onChange={(value) => handleFilterChange('date_from', value)}
                      placeholder="Select start date"
                      allowPastDates={true}
                      className="w-full"
                      size="md"
                    />
                  </div>

                  <div>
                    <DatePicker
                      label="To Date"
                      value={filters.date_to}
                      onChange={(value) => handleFilterChange('date_to', value)}
                      placeholder="Select end date"
                      allowPastDates={true}
                      minDate={filters.date_from || null}
                      className="w-full"
                      size="md"
                    />
                  </div>
                </div>
              </div>

              <DataTable
                data={bookings}
                columns={columns}
                actions={actions}
                onSearch={handleSearch}
                searchPlaceholder={
                  isPrivateTab
                    ? 'Search private bookings...'
                    : 'Search platform bookings...'
                }
                emptyMessage={
                  isPrivateTab
                    ? 'No private bookings found'
                    : 'No platform bookings found'
                }
                loading={isLoading}
                className="!border-0 !shadow-none"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBookings;
